<template>
    <div>

        <div class="deal-details-container" v-if="deal">
            <div class="deal-image-container">
                <a :href="(settings.isShabbat ? '#' : deal.dealUrl)" target="_blank" class="button-link" id="deal-url"
                    @click="goToDeal(deal, $event)">
                    <div class="deal-image-soldout" v-if="deal.isSoldOut"><img :src="`/Assets/img/soldout.png`" /></div>
                    <img id="img-url" :src="deal.imgUrl" :class="{ 'up-side-down': settings.purimMode }" />
                </a>
            </div>
            <div class="deal-info-container">
                <div class="deal-title">
                    <h1 id="deal-title">{{ deal.title }}</h1>
                </div>
                <div class="deal-last-update">Last Updated {{ formatDate(deal.updateDate) }}</div>
                <div class="deal-desc" id="deal-desc" v-if="deal && deal.description">
                    {{ deal.description }}
                </div>

                <div v-if="deal.hasPrice()">
                    <div class="crossed-price"><span v-if='deal.retailPrice'>{{ formatPrice(deal.retailPrice) }}</span>
                    </div>
                    <div class="deal-price-container">
                        <div class="deal-price">{{ formatPrice(deal.dealPrice) }}</div>
                        <div class="deal-discount" v-if="discount">-{{ discount.toFixed(0) }}%</div>
                    </div>
                </div>


                <div class="deal-promo-code" v-if="deal.promoCode">
                    <div class="deal-promo-code-title">Promo Code</div>
                    <div class="share-link-container">
                        <input id="promoCode" v-model="deal.promoCode" class="promo-code-input" readonly />
                        <a v-show="!copied" class="promo-copy-code" @click="copyToClipboard">COPY</a>
                        <a v-show="copied" class="promo-copy-code copied">COPIED</a>
                    </div>

                </div>
                <div class="mini-deal-buttons">
                    <a :href="(settings.isShabbat ? '#' : deal.dealUrl)" target="_blank" class="button-link" id="deal-url">
                        <button class="primary-button centered">
                            Get Deal
                        </button>
                    </a>

                    <input type="hidden" id="product-id" :value="deal.productId" />
                    <ShareButton :link="dealHelper.getDealLink(deal)" :messageTitle="'Check out this deal!'" :messageText="'Found this amazing deal on MommyDeals – thought you’d like it.'" />
                    <GetDealAlerts />
                </div>

                <div class="deal-amazon-img-container">
                    <DealLogoImage :deal="deal" class="logo-img" />
                </div>
                <div class="expiration-message" v-if="deal.expirationMessage">
                    {{ deal.expirationMessage }}
                </div>
                <br>
                <div id="deal-expired">
                    Disclosure: {{ settings.siteNameCaps }} is a participant in the Amazon Services LLC Associates
                    Program, an affiliate advertising program designed to provide a means for us to earn advertising
                    fees by advertising and linking to Amazon.com. As An Amazon Associate we earn from qualifying
                    purchases. Product prices and availability are accurate as of the date/time indicated and are
                    subject to change without notice including promo codes.
                </div>

            </div>
        </div>
    </div>
</template>



<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import api from '@/api'
import settings from '@/settings'
import util from '@/helpers/util'
import ga from '@/helpers/ga'
import clicky from '@/helpers/clicky'
import DealLogoImage from './DealLogoImage.vue'
import { DealDetails } from '@/classes/DealDetails'
import GetDealAlerts from './GetDealAlerts.vue'
import ShareButton from './ShareButton.vue'
import dealHelper from '@/helpers/deal-helper'

export default defineComponent({
    name: 'DealDetails',
    components: {
        DealLogoImage,
        GetDealAlerts,
        ShareButton
    },
    props: {
        dealId: {
            type: String,
            required: true
        }
    },
    setup(props) {
        const isLoading = ref(false)
        const currentUrl = ref(window.location.href)
        const deal = ref<DealDetails | null>(null)
        const dealUrl = ref<string | null>(null)
        const isParticipant = ref<boolean | null>(null)
        const descLength = ref(80)
        const copied = ref(false)

        const settingsComputed = computed(() => settings)
        const discount = computed(() => util.getDiscountPercentage(deal.value?.dealPrice, deal.value?.retailPrice))

        const formatDate = (date: string | Date) => {
            const d = new Date(date)
            return d.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            })
        }

        const formatPrice = (price: number): string => {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(price)
        }

        const getTimeAgo = (date: string | Date) => {
            const d = new Date(date)
            const now = new Date()
            const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000)

            if (diffInSeconds < 60) return 'just now'
            if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
            if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
            if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`
            if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`
            return `${Math.floor(diffInSeconds / 31536000)} years ago`
        }

        const toCurrency = (value: number | string, decimals: number = 2) => {
            const num = typeof value === 'string' ? parseFloat(value) : value
            return num.toFixed(decimals)
        }

        const init = async () => {
            isLoading.value = true
            try {
                const response = await api.getDeal(parseInt(props.dealId))
                // Convert plain object to DealDetails instance to ensure proper property mapping
                let tempDeal = new DealDetails(response.data)

                //check if an affiliate sent the traffic to us, if so, update the deal url
                let cookie_lastref = document.cookie.split(';').find(c => c.trim().startsWith('MMD_LastRef='))?.split('=')[1]

                // Ensure dealUrl exists - check both dealURL (from backend) and dealUrl (mapped)
                if (!tempDeal.dealUrl || tempDeal.dealUrl === '') {
                    if (tempDeal.dealURL && tempDeal.dealURL !== '') {
                        tempDeal.dealUrl = tempDeal.dealURL;
                    } else if ((response.data as any).dealURL && (response.data as any).dealURL !== '') {
                        tempDeal.dealUrl = (response.data as any).dealURL;
                    } else if ((response.data as any).dealUrl && (response.data as any).dealUrl !== '') {
                        tempDeal.dealUrl = (response.data as any).dealUrl;
                    }
                }

                // Only modify URLs if dealUrl exists and is not empty
                if (tempDeal.dealUrl && tempDeal.dealUrl !== '') {
                    if (settings.siteName == 'fivetowncentral' || (cookie_lastref && cookie_lastref.toLowerCase() == '5townscentral')) {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'tag', '5townscentral-20')
                    }
                    else if (settings.siteName == '5tcdeals') {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'tag', '5townscentral-20')
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'sharedid', '5tcdeals')
                    }
                    else if (settings.siteName == 'momazon' || (cookie_lastref && cookie_lastref.toLowerCase() == 'adir')) {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'tag', 'adirdeals-20')
                    }
                    else if (settings.siteName == 'alyssagoldwater' || (cookie_lastref && cookie_lastref.toLowerCase() == 'alyssagoldwater')) {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'tag', 'agoldwater-20')
                    }
                    else if (cookie_lastref && cookie_lastref.toLowerCase() == 'mmdfriedman') {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'tag', 'mmdfriedman-20')
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'sharedid', 'moshe')
                    }
                    else if (cookie_lastref && cookie_lastref.toLowerCase() == 'klugman') {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'tag', '18ventures-20')
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'sharedid', 'klugman')
                    }
                    else if (cookie_lastref && cookie_lastref.toLowerCase() == 'dovidklugman') {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'tag', 'mmdbraum-20')
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'sharedid', 'dovidklugman')
                    }
                    else if (cookie_lastref && cookie_lastref.toLowerCase() == 'braum') {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'tag', 'mmdbraum-20')
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'sharedid', 'braum')
                    }
                    else if (cookie_lastref && cookie_lastref.toLowerCase() == 'tempelberg') {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'tag', 'tempelberg-20')
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'sharedid', 'tempelberg')
                    }
                    else if (settings.siteName == 'mommydeals' && tempDeal.dealProvider == 'Amazon') {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'tag', 'mommydeals0a-20')
                    }
                    else if (settings.siteName == 'mommydeals' && tempDeal.dealProvider == 'Walmart') {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'sharedid', 'mommydeals')
                    }
                    else if (settings.siteName == 'petpetdeals' && tempDeal.dealProvider == 'Amazon') {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'tag', 'petpetdeals-20')
                    }
                    else if (settings.siteName == 'petpetdeals' && tempDeal.dealProvider == 'Walmart') {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'sharedid', 'petpetdeals')
                    }
                    else if (settings.siteName == 'chaideals' && tempDeal.dealProvider == 'Amazon') {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'tag', 'chaideals-20')
                    }
                    else if (settings.siteName == 'chaideals' && tempDeal.dealProvider == 'Walmart') {
                        tempDeal.dealUrl = util.replaceOrAddUrlParam(tempDeal.dealUrl, 'sharedid', 'chaideals')
                    }
                }

                if (tempDeal.isExpired) {
                    tempDeal.expirationMessage = 'Note: This deal might be expired since was it was posted ' + getTimeAgo(tempDeal.updateDate)
                }
                else {
                    tempDeal.expirationMessage = undefined
                }

                // Create final DealDetails instance, preserving the modified dealUrl
                // Clear dealURL to prevent constructor from overwriting our modifications
                const finalDealData = { ...tempDeal };
                delete (finalDealData as any).dealURL;
                deal.value = new DealDetails(finalDealData)
                document.title = deal.value.title + ' | ' + settings.siteNameCaps
                loadGoogleAds()
            } finally {
                isLoading.value = false
            }
        }

        const shortDesc = computed(() => {
            if (deal.value?.description && deal.value.description.length > descLength.value) {
                return deal.value.description.substring(0, descLength.value)
            }
            return deal.value?.description
        })

        const copyToClipboard = () => {
            const copyText = document.getElementById("promoCode") as HTMLInputElement
            copyText.select()
            document.execCommand("copy")
            copied.value = true
            setTimeout(() => {
                copied.value = false
            }, 2000)
        }

        const longDesc = () => {
            if (deal.value?.description) {
                descLength.value = deal.value.description.length
            }
        }

        const trackOutboundLink = (url: string) => {
            ga.event('click', { 'event_category': 'outbound', 'event_label': url, 'transport_type': 'beacon' })
            clicky.goal('DealClick2')
        }

        const goToDeal = (deal: DealDetails, event: Event) => {
            if (settings.isShabbat) {
                alert(`Thank you for your patience while ${settings.siteNameCaps} breaks for the Shabbos holiday.

Deal links will resume to work on ${settings.shabbatEnds}.`)
                event.preventDefault()
                event.stopPropagation()
            }
            else {
                // Track the outbound link - navigation happens naturally via anchor href
                trackOutboundLink(deal.dealUrl)
                // Don't prevent default - let the anchor navigate naturally
            }
        }

        const loadMetaTags = () => {
            const arrayOfTags = []

            if (deal.value?.title) arrayOfTags.push({ name: 'og:title', content: settings.siteNameCaps + ' | ' + deal.value.title })
            if (deal.value?.description) arrayOfTags.push({ name: 'og:description', content: deal.value.description })
            if (deal.value?.imgUrl) arrayOfTags.push({ name: 'og:image', content: deal.value.imgUrl })

            arrayOfTags.forEach(tag => {
                const metaTag = document.createElement('meta')
                metaTag.name = tag.name
                metaTag.content = tag.content
                document.getElementsByTagName('head')[0].appendChild(metaTag)
            })
        }

        const loadGoogleAds = () => {
            //document.querySelectorAll('.adsbygoogle').forEach(function () {
            //    (window.adsbygoogle = window.adsbygoogle || []).push({});
            //});
        }

        onMounted(() => {
            init()
        })

        return {
            isLoading,
            currentUrl,
            deal,
            dealUrl,
            isParticipant,
            descLength,
            copied,
            settings: settingsComputed,
            shortDesc,
            copyToClipboard,
            longDesc,
            trackOutboundLink,
            goToDeal,
            loadMetaTags,
            loadGoogleAds,
            formatDate,
            toCurrency,
            formatPrice,
            discount,
            dealHelper
        }
    }
})
</script>

<style scoped>
.deal-price {
    font-size: 1.75em;
}

.deal-desc {
    margin-bottom: 15px;
}

.deal-info-container{
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}



</style>
